import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getBillingCheckoutUrl } from '../../services/subscriptionService';

/**
 * Aviso de cobrança SaaS: inadimplência (past_due) ou bloqueio (suspended).
 */
export default function BillingStatusBanner({ access }) {
  const { t, i18n } = useTranslation('billing');
  const navigate = useNavigate();
  const [opening, setOpening] = useState(false);

  const openPayment = async () => {
    setOpening(true);
    try {
      const { data } = await getBillingCheckoutUrl();
      if (data?.url) {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      } else {
        navigate('/settings?tab=subscription');
      }
    } catch {
      navigate('/settings?tab=subscription');
    } finally {
      setOpening(false);
    }
  };

  if (!access) return null;

  const status = access.billing_status;
  if (status === 'super_admin' || status === 'platform_only' || status === 'unknown' || status === 'no_tenant') return null;

  if (access.blocked || status === 'suspended') {
    return (
      <div className="border-b border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900" role="alert">
        <div className="mx-auto flex max-w-content flex-wrap items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            <strong>{t('suspendedTitle')}</strong> {t('suspendedBody')}
          </span>
          <button
            type="button"
            onClick={openPayment}
            disabled={opening}
            className="ml-auto inline-flex items-center gap-1 font-medium text-red-800 underline"
          >
            <CreditCard className="h-4 w-4" />
            {opening ? t('common:opening') : t('payNow')}
          </button>
        </div>
      </div>
    );
  }

  if (status === 'past_due') {
    const grace = access.grace_ends_at
      ? new Date(access.grace_ends_at).toLocaleDateString(i18n.language)
      : null;
    return (
      <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
        <div className="mx-auto flex max-w-content flex-wrap items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            <strong>{t('pastDueTitle')}</strong>
            {grace ? t('pastDueUntil', { date: grace }) : t('pastDueGeneric')}
          </span>
          <button
            type="button"
            onClick={openPayment}
            disabled={opening}
            className="ml-auto inline-flex items-center gap-1 font-medium text-amber-900 underline"
          >
            <CreditCard className="h-4 w-4" />
            {opening ? t('common:opening') : t('payOrReview')}
          </button>
        </div>
      </div>
    );
  }

  return null;
}
